import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const NAICS_MAP = {
  'Technology':                  {
    naics: { code: '541511', title: 'Custom Computer Programming Services', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '7371',   title: 'Computer Programming Services' },
  },
  'Marketing & Advertising':     {
    naics: { code: '541810', title: 'Advertising Agencies', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '7311',   title: 'Advertising Agencies' },
  },
  'Consulting':                  {
    naics: { code: '541611', title: 'Administrative & General Management Consulting', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8742',   title: 'Management Consulting Services' },
  },
  'Finance & Accounting':        {
    naics: { code: '541211', title: 'Offices of Certified Public Accountants', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8721',   title: 'Accounting, Auditing & Bookkeeping' },
  },
  'Healthcare':                  {
    naics: { code: '621111', title: 'Offices of Physicians', sector: '62 - Health Care & Social Assistance' },
    sic:   { code: '8011',   title: 'Offices & Clinics of Medical Doctors' },
  },
  'Legal':                       {
    naics: { code: '541110', title: 'Offices of Lawyers', sector: '54 - Professional, Scientific & Technical Services' },
    sic:   { code: '8111',   title: 'Legal Services' },
  },
  'Real Estate':                 {
    naics: { code: '531210', title: 'Offices of Real Estate Agents & Brokers', sector: '53 - Real Estate & Rental & Leasing' },
    sic:   { code: '6531',   title: 'Real Estate Agents & Managers' },
  },
  'Construction':                {
    naics: { code: '236220', title: 'Commercial & Institutional Building Construction', sector: '23 - Construction' },
    sic:   { code: '1542',   title: 'General Contractors — Nonresidential Buildings' },
  },
  'Education':                   {
    naics: { code: '611710', title: 'Educational Support Services', sector: '61 - Educational Services' },
    sic:   { code: '8299',   title: 'Schools & Educational Services, NEC' },
  },
  'Non-Profit':                  {
    naics: { code: '813319', title: 'Other Social Advocacy Organizations', sector: '81 - Other Services' },
    sic:   { code: '8399',   title: 'Social Services, NEC' },
  },
  'Retail':                      {
    naics: { code: '452990', title: 'All Other General Merchandise Retailers', sector: '44-45 - Retail Trade' },
    sic:   { code: '5399',   title: 'Retail Stores, NEC' },
  },
  'Manufacturing':               {
    naics: { code: '339999', title: 'All Other Miscellaneous Manufacturing', sector: '31-33 - Manufacturing' },
    sic:   { code: '3999',   title: 'Manufacturing Industries, NEC' },
  },
  'Media & Entertainment':       {
    naics: { code: '512110', title: 'Motion Picture & Video Production', sector: '51 - Information' },
    sic:   { code: '7812',   title: 'Motion Picture & Video Tape Production' },
  },
  'Other':                       null,
};

const INDUSTRIES = [
  'Technology', 'Marketing & Advertising', 'Consulting', 'Finance & Accounting',
  'Healthcare', 'Legal', 'Real Estate', 'Construction', 'Education',
  'Non-Profit', 'Retail', 'Manufacturing', 'Media & Entertainment', 'Other',
];

const PLAN_INFO = {
  free:     { label: 'Free',     color: '#64748b', bg: '#f8fafc',  desc: 'Forever free · Up to 2 projects · Up to 1 user' },
  team:     { label: 'Team',     color: '#2563eb', bg: '#eff6ff',  desc: '$7/user/month · Unlimited projects & users' },
  business: { label: 'Business', color: '#7c3aed', bg: '#f5f3ff',  desc: '$17/user/month · AI Agent + all features' },
};

export default function WorkspacePage() {
  const [workspace, setWorkspace] = useState(null);
  const [form,      setForm]      = useState({});
  const [naicsCustom, setNaicsCustom] = useState(false);
  const [sicCustom, setSicCustom] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg,       setMsg]       = useState('');
  const [newSlug,   setNewSlug]   = useState('');
  const [slugAvail, setSlugAvail] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [savingSlug,   setSavingSlug]   = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);
  const [upgrading,    setUpgrading]    = useState(null); // 'team' | 'business' | null
  const [upgradeError, setUpgradeError] = useState('');
  const [checkoutMsg,  setCheckoutMsg]  = useState(null);

  const handleUpgrade = async (plan) => {
    setUpgrading(plan);
    setUpgradeError('');
    try {
      const res = await api.post('/gpf/v1/billing/create-checkout-session', { plan });
      window.location.href = res.data.url; // redirect to Stripe Checkout
    } catch (e) {
      setUpgradeError(e.response?.data?.message || 'Could not start checkout. Please try again.');
      setUpgrading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await api.post('/gpf/v1/billing/create-portal-session');
      window.location.href = res.data.url;
    } catch (e) {
      setCheckoutMsg({ type: 'error', text: e.response?.data?.message || 'Could not open billing portal.' });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('checkout');
    const sessionId = params.get('session_id');

    if (params.get('upgrade') === '1') {
      setShowUpgrade(true);
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (status === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      if (sessionId) {
        setCheckoutMsg({ type: 'success', text: '✅ Payment received — activating your plan...' });
        api.post('/gpf/v1/billing/verify-session', { session_id: sessionId })
          .then(() => {
            setCheckoutMsg({ type: 'success', text: '✅ Subscription activated!' });
            fetchWorkspace(); // refresh plan info shown on this page
          })
          .catch((e) => {
            setCheckoutMsg({ type: 'error', text: e.response?.data?.message || 'Payment succeeded, but we could not confirm activation. Please refresh in a moment or contact support.' });
          });
      } else {
        setCheckoutMsg({ type: 'success', text: '✅ Subscription activated! Your plan will update within a few seconds.' });
      }
    } else if (status === 'cancelled') {
      setCheckoutMsg({ type: 'error', text: 'Checkout was cancelled — no charge was made.' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);
  const [error,     setError]     = useState('');

  useEffect(() => { fetchWorkspace(); }, []);

  const fetchWorkspace = async () => {
    try {
      const res = await api.get('/gpf/v1/workspace');
      setWorkspace(res.data);
      setForm(res.data);
    } catch(e) { console.error(e); }
  };

  const checkSlug = async (slug) => {
    const clean = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setNewSlug(clean);
    if (!clean || clean === workspace?.workspace_slug) { setSlugAvail(null); return; }
    setCheckingSlug(true);
    try {
      const res = await api.get(`/gpf/v1/workspace/check/${clean}`);
      setSlugAvail(res.data.available);
    } catch { setSlugAvail(null); }
    finally { setCheckingSlug(false); }
  };

  const handleSaveSlug = async () => {
    if (!newSlug || !slugAvail) return;
    setSavingSlug(true);
    try {
      await api.post('/gpf/v1/workspace/slug', { slug: newSlug });
      setMsg('Workspace URL updated! Share the new URL with your team.');
      fetchWorkspace();
      setNewSlug('');
      setSlugAvail(null);
      setTimeout(() => setMsg(''), 4000);
    } catch(e) { setError('Failed to update URL.'); }
    finally { setSavingSlug(false); }
  };

  const handleSave = async () => {
    setSaving(true); setMsg(''); setError('');
    try {
      await api.post('/gpf/v1/workspace', form);
      setMsg('Workspace settings saved!');
      fetchWorkspace();
      setTimeout(() => setMsg(''), 3000);
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await api.post('/gpf/v1/workspace/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(prev => ({ ...prev, company_logo: res.data.url }));
      setMsg('Logo uploaded!');
      setTimeout(() => setMsg(''), 3000);
    } catch(e) { setError('Logo upload failed.'); }
    finally { setUploading(false); }
  };

  const plan    = workspace?.plan || 'free';
  const planInfo = PLAN_INFO[plan] || PLAN_INFO.free;
  const trialEnd = workspace?.trial_end;
  const daysLeft = (plan !== 'free' && trialEnd) ? Math.max(0, Math.ceil((new Date(trialEnd) - new Date()) / (1000*60*60*24))) : 0;

  if (!workspace) return <Layout><div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading workspace...</div></Layout>;

  return (
    <Layout>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Workspace Settings</h1>
            <p style={styles.sub}>Manage your company profile and account settings</p>
          </div>
        </div>

        {/* Plan card */}
        <div style={{ ...styles.planCard, background: planInfo.bg, borderColor: planInfo.color + '33' }}>
          <div style={styles.planLeft}>
            <span style={{ ...styles.planBadge, background: planInfo.color, color: 'white' }}>{planInfo.label}</span>
            <span style={styles.planDesc}>{planInfo.desc}</span>
            {daysLeft > 0 && (
              <span style={styles.trialBadge}>⏱ {daysLeft} days left in trial</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button style={styles.upgradeBtn} onClick={() => setShowUpgrade(true)}>
              {plan === 'free' ? 'Upgrade Plan →' : 'Change Plan →'}
            </button>
            {plan !== 'free' && (
              <button style={{ ...styles.upgradeBtn, background: 'white', color: planInfo.color, border: `1.5px solid ${planInfo.color}` }} onClick={handleManageBilling}>
                Manage Billing
              </button>
            )}
          </div>
        </div>
        {checkoutMsg && (
          <div style={{ background: checkoutMsg.type === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${checkoutMsg.type === 'success' ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 13, color: checkoutMsg.type === 'success' ? '#16a34a' : '#dc2626' }}>
            {checkoutMsg.text}
          </div>
        )}

        {msg   && <div style={styles.successBox}>✅ {msg}</div>}
        {error && <div style={styles.errorBox}>⚠️ {error}</div>}

        <div style={styles.grid}>
          {/* Left column */}
          <div style={styles.col}>
            {/* Logo */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Company Logo</h3>
              <p style={styles.cardDesc}>Shown in the sidebar for all team members</p>
              <div style={styles.logoArea}>
                {form.company_logo ? (
                  <img src={form.company_logo} alt="logo" style={styles.logoImg} />
                ) : (
                  <div style={styles.logoPlaceholder}>
                    <span style={{ fontSize: 32 }}>🏢</span>
                    <span style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>No logo yet</span>
                  </div>
                )}
                <div style={styles.logoActions}>
                  <label style={styles.uploadBtn}>
                    {uploading ? 'Uploading...' : '📷 Upload Logo'}
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploading} />
                  </label>
                  {form.company_logo && (
                    <button style={styles.removeBtn} onClick={() => setForm(prev => ({ ...prev, company_logo: '' }))}>Remove</button>
                  )}
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>PNG, JPG up to 2MB · Recommended: 200×200px</p>
                </div>
              </div>
            </div>

            {/* Workspace URL */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Workspace URL</h3>
              <p style={styles.cardDesc}>Your team's dedicated address</p>
              <div style={styles.slugBox}>
                <span style={styles.slugText}>
                  {workspace.workspace_slug
                    ? `${workspace.workspace_slug}.goprojectflow.com`
                    : 'app.goprojectflow.com'}
                </span>
              </div>
              <div style={{ marginTop: 14 }}>
                <label style={styles.label}>Change Workspace URL</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    placeholder="e.g. acme-corp"
                    value={newSlug}
                    onChange={e => checkSlug(e.target.value)}
                  />
                  <button
                    style={{ ...styles.saveBtn, opacity: (!slugAvail || savingSlug) ? 0.5 : 1 }}
                    onClick={handleSaveSlug}
                    disabled={!slugAvail || savingSlug}
                  >{savingSlug ? 'Saving...' : 'Update'}</button>
                </div>
                {newSlug && (
                  <p style={{ fontSize: 12, margin: '6px 0 0', color: checkingSlug ? '#94a3b8' : slugAvail ? '#16a34a' : '#dc2626' }}>
                    {checkingSlug ? 'Checking...' : slugAvail ? `✓ ${newSlug}.goprojectflow.com is available` : `✗ Already taken`}
                  </p>
                )}
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>
                  Lowercase letters, numbers and hyphens only
                </p>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div style={styles.col}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Company Information</h3>
              <div style={styles.formGrid}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Company Name *</label>
                  <input style={styles.input} value={form.company_name || ''} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="Acme Corporation" />
                </div>
                <div>
                  <label style={styles.label}>Industry</label>
                  <select
                    style={styles.input}
                    value={form.company_industry || ''}
                    onChange={e => {
                      const industry = e.target.value;
                      const match = NAICS_MAP[industry];
                      setForm({
                        ...form,
                        company_industry: industry,
                        naics_code:  match?.naics?.code  || '',
                        naics_title: match?.naics?.title || '',
                        sic_code:    match?.sic?.code    || '',
                        sic_title:   match?.sic?.title   || '',
                      });
                      setNaicsCustom(false);
                      setSicCustom(false);
                    }}
                  >
                    <option value="">Select industry...</option>
                    {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={form.company_phone || ''} onChange={e => setForm({...form, company_phone: e.target.value})} placeholder="+1 (555) 000-0000" />
                </div>
                {form.company_industry && form.company_industry !== 'Other' && (
                  <>
                    <div>
                      <label style={styles.label}>NAICS Code</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          style={{ ...styles.input, flex: 1, fontFamily: 'monospace', background: naicsCustom ? 'white' : '#f8fafc' }}
                          value={form.naics_code || ''}
                          onChange={e => { setForm({...form, naics_code: e.target.value}); setNaicsCustom(true); }}
                        />
                        <a
                          href="https://www.census.gov/naics/?year=2022"
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: '#2563eb', whiteSpace: 'nowrap', textDecoration: 'none' }}
                        >Look up →</a>
                      </div>
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>
                        {form.naics_title ? `${form.naics_title}` : 'Auto-suggested — edit if you know your exact code'}
                      </p>
                    </div>
                    <div>
                      <label style={styles.label}>SIC Code</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          style={{ ...styles.input, flex: 1, fontFamily: 'monospace', background: sicCustom ? 'white' : '#f8fafc' }}
                          value={form.sic_code || ''}
                          onChange={e => { setForm({...form, sic_code: e.target.value}); setSicCustom(true); }}
                        />
                        <a
                          href="https://www.osha.gov/data/sic-search"
                          target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color: '#2563eb', whiteSpace: 'nowrap', textDecoration: 'none' }}
                        >Look up →</a>
                      </div>
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>
                        {form.sic_title ? `${form.sic_title}` : 'Auto-suggested — edit if you know your exact code'}
                      </p>
                    </div>
                  </>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Address</label>
                  <input style={styles.input} value={form.company_address || ''} onChange={e => setForm({...form, company_address: e.target.value})} placeholder="123 Main St, City, State, Country" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Website</label>
                  <input style={styles.input} value={form.company_website || ''} onChange={e => setForm({...form, company_website: e.target.value})} placeholder="https://yourcompany.com" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Billing Contact Email</label>
                  <input style={styles.input} type="email" value={form.billing_contact || ''} onChange={e => setForm({...form, billing_contact: e.target.value})} placeholder="billing@yourcompany.com" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>

            {/* Legal Business Details — used to auto-fill government forms */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Legal Business Details</h3>
              <p style={styles.cardDesc}>Used to auto-fill licenses, permits, and government forms</p>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Legal Business Name</label>
                  <input style={styles.input} value={form.legal_name || ''} onChange={e => setForm({...form, legal_name: e.target.value})} placeholder="Acme Corporation LLC" />
                </div>
                <div>
                  <label style={styles.label}>DBA / Trade Name</label>
                  <input style={styles.input} value={form.dba_name || ''} onChange={e => setForm({...form, dba_name: e.target.value})} placeholder="If different from legal name" />
                </div>
                <div>
                  <label style={styles.label}>Entity Type</label>
                  <select style={styles.input} value={form.entity_type || ''} onChange={e => setForm({...form, entity_type: e.target.value})}>
                    <option value="">Select type...</option>
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                    <option value="s_corp">S-Corporation</option>
                    <option value="partnership">Partnership</option>
                    <option value="nonprofit">Nonprofit</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>EIN (Federal Tax ID)</label>
                  <input style={styles.input} value={form.ein || ''} onChange={e => setForm({...form, ein: e.target.value})} placeholder="XX-XXXXXXX" />
                </div>
                <div>
                  <label style={styles.label}>State of Formation</label>
                  <input style={styles.input} value={form.formation_state || ''} onChange={e => setForm({...form, formation_state: e.target.value})} placeholder="e.g. Georgia" />
                </div>
                <div>
                  <label style={styles.label}>Estimated Employees</label>
                  <input style={styles.input} type="number" value={form.employee_count || ''} onChange={e => setForm({...form, employee_count: e.target.value})} placeholder="e.g. 5" />
                </div>
                <div>
                  <label style={styles.label}>Planned Opening Date</label>
                  <input style={styles.input} type="date" value={form.opening_date || ''} onChange={e => setForm({...form, opening_date: e.target.value})} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ ...styles.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.mailing_same !== '0'} onChange={e => setForm({...form, mailing_same: e.target.checked ? '1' : '0'})} style={{ margin: 0 }} />
                    Mailing address is the same as physical address
                  </label>
                </div>
                {form.mailing_same === '0' && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Mailing Address</label>
                    <input style={styles.input} value={form.mailing_address || ''} onChange={e => setForm({...form, mailing_address: e.target.value})} placeholder="If different from physical address above" />
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>

            {/* Primary Applicant — the contact person named on forms */}
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Primary Applicant</h3>
              <p style={styles.cardDesc}>The person named as applicant/contact on licenses and permits</p>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label}>Full Name</label>
                  <input style={styles.input} value={form.applicant_name || ''} onChange={e => setForm({...form, applicant_name: e.target.value})} placeholder="Jane Smith" />
                </div>
                <div>
                  <label style={styles.label}>Title</label>
                  <input style={styles.input} value={form.applicant_title || ''} onChange={e => setForm({...form, applicant_title: e.target.value})} placeholder="Owner / Manager / CEO" />
                </div>
                <div>
                  <label style={styles.label}>Phone</label>
                  <input style={styles.input} value={form.applicant_phone || ''} onChange={e => setForm({...form, applicant_phone: e.target.value})} placeholder="+1 (555) 000-0000" />
                </div>
                <div>
                  <label style={styles.label}>Email</label>
                  <input style={styles.input} type="email" value={form.applicant_email || ''} onChange={e => setForm({...form, applicant_email: e.target.value})} placeholder="jane@yourcompany.com" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Upgrade Modal */}
      {showUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Upgrade Your Plan</h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>Choose the plan that fits your team</p>
              </div>
              <button onClick={() => setShowUpgrade(false)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '24px 28px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Team Plan */}
              <div style={{ border: '2px solid #2563eb', borderRadius: 12, padding: 24, background: '#eff6ff' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Most Popular</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Team</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>For growing teams that need more</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                  <span style={{ fontSize: 40, fontWeight: 800 }}>$7</span>
                  <span style={{ fontSize: 14, color: '#64748b' }}>/user/month</span>
                </div>
                <p style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, marginBottom: 20 }}>Billed yearly · ~$84/user per year</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['Unlimited projects','Unlimited users','Tasks, notes & time tracking','5 GB file storage','Documents & rich text editor','Calendar & meetings','Reports & insights'].map(f => (
                    <li key={f} style={{ fontSize: 13, display: 'flex', gap: 8 }}><span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>{f}</li>
                  ))}
                </ul>
                <button onClick={() => handleUpgrade('team')} disabled={upgrading} style={{ width: '100%', display: 'block', textAlign: 'center', padding: '12px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: upgrading ? 0.6 : 1 }}>
                  {upgrading === 'team' ? 'Redirecting to checkout...' : 'Upgrade to Team →'}
                </button>
              </div>
              {/* Business Plan */}
              <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Full Features</div>
                <h3 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Business</h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>AI Agent + full control</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                  <span style={{ fontSize: 40, fontWeight: 800 }}>$17</span>
                  <span style={{ fontSize: 14, color: '#64748b' }}>/user/month</span>
                </div>
                <p style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, marginBottom: 20 }}>Billed yearly · ~$204/user per year</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['Everything in Team','🤖 AI Agent included','Unlimited file storage','Role-based permissions','Priority support'].map(f => (
                    <li key={f} style={{ fontSize: 13, display: 'flex', gap: 8 }}><span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>{f}</li>
                  ))}
                </ul>
                <button onClick={() => handleUpgrade('business')} disabled={upgrading} style={{ width: '100%', display: 'block', textAlign: 'center', padding: '12px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: upgrading ? 0.6 : 1 }}>
                  {upgrading === 'business' ? 'Redirecting to checkout...' : 'Upgrade to Business →'}
                </button>
              </div>
            </div>
            {upgradeError && <p style={{ color: '#dc2626', fontSize: 13, textAlign: 'center', margin: '0 28px' }}>{upgradeError}</p>}
            <div style={{ padding: '16px 28px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>Payments securely processed by Stripe. Questions? Email <a href="mailto:billing@goprojectflow.com" style={{ color: '#2563eb' }}>billing@goprojectflow.com</a></p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 960, margin: '0 auto' },
  header:        { marginBottom: 24 },
  title:         { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:           { fontSize: 14, color: '#64748b', margin: '4px 0 0' },
  planCard:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderRadius: 12, border: '1.5px solid', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  planLeft:      { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  planBadge:     { fontSize: 12, fontWeight: 700, padding: '3px 12px', borderRadius: 99 },
  planDesc:      { fontSize: 13, color: '#374151' },
  trialBadge:    { fontSize: 12, color: '#d97706', background: '#fffbeb', padding: '2px 10px', borderRadius: 99, fontWeight: 600 },
  upgradeBtn:    { padding: '8px 18px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' },
  successBox:    { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#16a34a' },
  errorBox:      { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' },
  grid:          { display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20, alignItems: 'start' },
  col:           { display: 'flex', flexDirection: 'column', gap: 20 },
  card:          { background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardTitle:     { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 4px' },
  cardDesc:      { fontSize: 13, color: '#64748b', margin: '0 0 16px' },
  logoArea:      { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  logoImg:       { width: 80, height: 80, borderRadius: 10, objectFit: 'contain', border: '1px solid #e2e8f0' },
  logoPlaceholder:{ width: 80, height: 80, borderRadius: 10, background: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  logoActions:   { display: 'flex', flexDirection: 'column', gap: 6 },
  uploadBtn:     { fontSize: 13, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '7px 14px', borderRadius: 8, border: '1px solid #bfdbfe', cursor: 'pointer', display: 'inline-block' },
  removeBtn:     { fontSize: 12, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' },
  slugBox:       { background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' },
  slugText:      { fontSize: 14, fontWeight: 600, color: '#2563eb', fontFamily: 'monospace' },
  formGrid:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  label:         { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  input:         { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  saveBtn:       { padding: '10px 24px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};